/**
 * 무대 위의 꾸미기 도구 - 꾸미기 진입에 세우고 외우기 진입에 치운다.
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(꾸미기 1분) · 4.1절(인당 배치 수)
 *            `이상한방-상태모델-v0.1.md` 9.1절(하한 미달이면 프리셋 변주) · 9.4절(감춘 도구는 언젠가 다시 드러난다)
 *            로드맵 M10
 *
 * `stageChanger.ts`의 형제다 - 하는 일은 조립 하나이고 규칙은 없다. 도구(`input/decorateTool.ts`) · 띠
 * (`ui/decorateBar.ts`) · 놓을 자리 고스트를 세션의 문(`MatchDecoratePort`)에 묶는다.
 *
 * ## 세우는 순간이 곧 규약이다 - `hidden`이 아니라 `dispose`다
 *
 * 도구는 `phaseEnter{decorate}`에 서고 다른 페이즈에 들어서는 순간 통째로 사라진다. 꾸미기 띠가 수색까지 남으면
 * 그것은 쏘는 화면 위의 배치 도구이고, 감춰 두면 언젠가 다시 드러난다(9.4절).
 *
 * ## 바꾸기 도구와 **같이 서지 않는다**
 *
 * 두 페이즈는 배타적이라 두 도구가 동시에 설 일이 없다. 그래도 조건을 각자 두는 대신 각 도구가 뷰의 불리언 하나로
 * (`self.canDecorate` · `self.canChange`) 갈리게 두었다 - 두 조건을 한 곳에서 배타로 적으면 그것이 곧 페이즈 표의
 * 두 번째 사본이다. 손(`app/inputLayer.ts`)은 둘 다 물어보고 서 있는 쪽에 탭을 준다.
 */

import type { Object3D } from 'three'

import { PALETTE } from '../catalog/palette'
import type { Catalog } from '../core/room'
import type { PlayerId, RoomBounds } from '../core/types'
import type { MatchDecoratePort } from '../game/matchSessionDecorate'
import type { MatchPhase } from '../game/match/matchTypes'
import { createDecorateTool, type DecorateTool } from '../input/decorateTool'
import { EXACT_TAP_RADIUS_PX, pickAt } from '../input/tapPicker'
import { createGhostMesh, updateGhost } from '../render/gridHelper'
import type { SceneCtx } from '../render/scene'
import { createDecorateBar } from '../ui/decorateBar'

export type StageDecorateDeps = Readonly<{
  ctx: SceneCtx
  catalog: Catalog
  bounds: RoomBounds
  seed: string
  me: PlayerId
  mount: HTMLElement
  port: MatchDecoratePort
}>

export type StageDecorate = Readonly<{
  /** 지금 서 있는 도구. 없으면 `null` - 손과 세션이 이것으로 "지금 꾸미는 중인가"를 안다. */
  current: () => DecorateTool | null
  onPhaseEnter: (phase: MatchPhase) => void
  dispose: () => void
}>

export function createStageDecorate(deps: StageDecorateDeps): StageDecorate {
  const { ctx, catalog, bounds, port } = deps

  let mounted: Readonly<{ tool: DecorateTool; dispose: () => void }> | null = null

  function unmount(): void {
    mounted?.dispose()
    mounted = null
  }

  function mount(): void {
    unmount()
    // 방이 아직 안 왔으면 세울 것이 없다(`stageChanger`와 같은 길 - 호스트는 `rooms`를 `step`보다 먼저 보낸다).
    if (port.room() === null) return

    const ghost: Object3D = createGhostMesh()
    ghost.visible = false
    ctx.scene.add(ghost)

    // 띠의 칩은 도구를 부른다. 도구가 띠보다 뒤에 만들어지므로 클로저로 늦게 잡는다(서로를 인자로 요구하면 순환이다).
    let tool: DecorateTool | null = null
    const bar = createDecorateBar({
      mount: deps.mount,
      handlers: { onCatalog: (id) => tool?.pickCatalog(id) },
    })

    tool = createDecorateTool({
      catalog,
      bounds,
      palette: PALETTE,
      seed: deps.seed,
      me: deps.me,
      room: port.room,
      view: port.snapshot,
      // 바꾸기 도구와 **같은 한 줄**이다 - 걷기의 물건 고르기도 배치와 같은 정확함이라 반경 0이다(`stageChanger.ts`).
      pick: (x, y, room, mode) =>
        pickAt(ctx, x, y, room, catalog, bounds, mode === 'place' ? { mode: 'place' } : { mode: 'search', radiusPx: EXACT_TAP_RADIUS_PX }),
      send: port.send,
      onView: (view) => bar.render(view),
      onLocalReject: port.showRejection,
      // 배치 에디터·바꾸기 도구와 같은 고스트다(`render/gridHelper.updateGhost`) - 좌표를 다시 만들지 않는다.
      onPreview: (preview) => {
        const entry = preview === null ? undefined : catalog.get(preview.catalogId)
        if (preview === null || entry === undefined) {
          ghost.visible = false
          return
        }
        updateGhost(ghost, preview.cell, entry, preview.rot, preview.valid)
      },
    })

    const built = tool
    mounted = {
      tool: built,
      dispose(): void {
        built.dispose()
        bar.dispose()
        ghost.removeFromParent()
      },
    }
  }

  return {
    current: () => mounted?.tool ?? null,

    onPhaseEnter(): void {
      // "꾸미기 페이즈이고 좌석이 있는가"는 호스트가 내린 뷰의 불리언 하나(`self.canDecorate`)다 -
      // 여기서 `phase === 'decorate'`를 다시 대면 게이트의 사본이다.
      if (port.snapshot().active) mount()
      else unmount()
    },

    dispose: unmount,
  }
}
